/**
 * Sidebar - Left navigation menu showing boards and spaces
 *
 * Matches Blinkin's clean, minimal sidebar design
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { KnowledgeSpace } from '@deep-agents/shared-types';
import { apiFetch } from '../../lib/api';

interface Board {
  id: string;
  name: string;
  updatedAt?: number;
}

export function Sidebar() {
  const navigate = useNavigate();
  const [boards, setBoards] = useState<Board[]>([]);
  const [isLoadingBoards, setIsLoadingBoards] = useState(true);
  const [spaces, setSpaces] = useState<KnowledgeSpace[]>([]);
  const [isLoadingSpaces, setIsLoadingSpaces] = useState(true);

  // Fetch boards from API
  useEffect(() => {
    async function fetchBoards() {
      try {
        const response = await apiFetch('/boards');
        if (response.ok) {
          const data = await response.json();
          setBoards(data?.data?.boards || []);
        }
      } catch (error) {
        console.error('Failed to fetch boards:', error);
      } finally {
        setIsLoadingBoards(false);
      }
    }
    fetchBoards();
  }, []);

  useEffect(() => {
    async function fetchSpaces() {
      try {
        const response = await apiFetch('/kb/spaces');
        if (response.ok) {
          const data = await response.json();
          setSpaces(data?.data?.spaces || []);
        }
      } catch (error) {
        console.error('Failed to fetch spaces:', error);
      } finally {
        setIsLoadingSpaces(false);
      }
    }
    fetchSpaces();
  }, []);

  const handleBoardClick = (boardId: string) => {
    navigate(`/boards/${boardId}`);
  };

  const handleSpaceClick = (spaceId: string) => {
    navigate(`/knowledge/${spaceId}`);
  };

  const handleNewBoard = async () => {
    try {
      const response = await apiFetch('/boards', {
        method: 'POST',
        body: JSON.stringify({
          name: 'Untitled Board',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const boardId = data?.data?.id;
        if (boardId) {
          navigate(`/boards/${boardId}`);
        }
      }
    } catch (error) {
      console.error('Failed to create board:', error);
    }
  };

  return (
    <aside className="sidebar">
      {/* Logo/Brand */}
      <div className="sidebar-header">
        <button
          type="button"
          className="home-button"
          onClick={() => navigate('/')}
          title="Home"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        </button>
        <h2 className="sidebar-brand">Deep Agents</h2>
      </div>

      {/* Boards Section */}
      <nav className="sidebar-section">
        <div className="section-header">
          <span className="section-title">BOARDS</span>
          <button
            type="button"
            className="new-button"
            onClick={handleNewBoard}
            title="New Board"
          >
            +
          </button>
        </div>

        <ul className="nav-list">
          {isLoadingBoards ? (
            <li className="nav-item loading">Loading...</li>
          ) : boards.length === 0 ? (
            <li className="nav-item empty">No boards yet</li>
          ) : (
            boards.map(board => (
              <li key={board.id}>
                <button
                  type="button"
                  className="nav-item"
                  onClick={() => handleBoardClick(board.id)}
                >
                  <svg className="nav-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="9" y1="3" x2="9" y2="21" />
                  </svg>
                  <span className="nav-label">{board.name}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      </nav>

      {/* Spaces Section */}
      <nav className="sidebar-section">
        <div className="section-header">
          <span className="section-title">SPACES</span>
          <button
            type="button"
            className="new-button"
            title="New Space"
            onClick={() => navigate('/knowledge')}
          >
            +
          </button>
        </div>

        <ul className="nav-list">
          <li>
            <button
              type="button"
              className="nav-item"
              onClick={() => navigate('/knowledge')}
            >
              <svg className="nav-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 7h6v10H3zM15 7h6v10h-6zM9 5h6v14H9z" />
              </svg>
              <span className="nav-label">All Spaces</span>
            </button>
          </li>
          {isLoadingSpaces ? (
            <li className="nav-item loading">Loading...</li>
          ) : spaces.length === 0 ? (
            <li className="nav-item empty">No spaces yet</li>
          ) : (
            spaces.map(space => (
              <li key={space.id}>
                <button
                  type="button"
                  className="nav-item"
                  onClick={() => handleSpaceClick(space.id)}
                >
                  {space.type === 'personal' ? (
                    <svg className="nav-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  ) : space.type === 'team' ? (
                    <svg className="nav-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  ) : (
                    <svg className="nav-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <line x1="9" y1="3" x2="9" y2="21" />
                    </svg>
                  )}
                  <span className="nav-label">{space.name}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      </nav>

      <style>{`
        .sidebar {
          width: 240px;
          height: 100vh;
          background: #fafafa;
          border-right: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          padding: 20px 0;
          position: fixed;
          left: 0;
          top: 0;
          overflow-y: auto;
        }

        .sidebar-header {
          padding: 0 20px 24px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .home-button {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: #6b7280;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s;
          flex-shrink: 0;
        }

        .home-button:hover {
          background: #f3f4f6;
          color: #000;
        }

        .sidebar-brand {
          font-size: 18px;
          font-weight: 600;
          color: #000;
          margin: 0;
          letter-spacing: -0.01em;
          flex: 1;
        }

        .sidebar-section {
          padding: 20px 0;
          border-bottom: 1px solid #e5e7eb;
        }

        .sidebar-section:last-child {
          border-bottom: none;
          flex: 1;
        }

        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 20px 12px;
        }

        .section-title {
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .new-button {
          width: 20px;
          height: 20px;
          border-radius: 4px;
          border: none;
          background: transparent;
          color: #6b7280;
          font-size: 18px;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s;
        }

        .new-button:hover:not(:disabled) {
          background: #f3f4f6;
          color: #000;
        }

        .new-button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .nav-list {
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .nav-list li {
          margin: 0;
        }

        .nav-item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 20px;
          background: transparent;
          border: none;
          color: #374151;
          font-size: 14px;
          cursor: pointer;
          transition: background 0.15s;
          text-align: left;
        }

        .nav-item:hover:not(:disabled):not(.loading):not(.empty) {
          background: #f3f4f6;
        }

        .nav-item:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .nav-item.loading,
        .nav-item.empty {
          color: #9ca3af;
          font-size: 13px;
          cursor: default;
        }

        .nav-icon {
          flex-shrink: 0;
          color: #9ca3af;
          transition: color 0.15s;
        }

        .nav-item:hover:not(:disabled) .nav-icon {
          color: #6b7280;
        }

        .nav-label {
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        @media (max-width: 768px) {
          .sidebar {
            width: 200px;
          }
        }
      `}</style>
    </aside>
  );
}
